import React, { useState, useEffect, useContext } from "react";
import {
  View,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Platform,
  Button,
  PixelRatio,
} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import { ApiService, setAuthToken } from "../../service/ApiService";
import FormattedText from "../../components/FormattedText";
import SkeletonLoader from "../../components/SkeletonLoader";
import { IPropertyData } from "../../types/IPropertyDataType";
import commonStyles from "../../styles/commonStyles";
import OverflowTooltipText from "../../components/OverflowTooltipText";
import { Switch } from "react-native-gesture-handler";
import PropertiesStyle from "./PropertiesStyle";
import PropertyMap from "./PropertyMaps/PropertyMaps";

import PropertyMapsMobile from "./PropertyMapMobile/PropertyMapsMobile";
import globalEventEmitter from "../../components/eventEmitter";

import { useIsFocused } from "@react-navigation/native";
import { RefreshContext } from "../../contexts/refreshContext";
import { getUserIdToUse } from "../../utils/getUserIdToUse";
import { SafeAreaView } from "react-native-safe-area-context";
import AppText from "../../components/AppText";

interface InvestmentPageProps {
  navigation: {
    navigate: (screen: string, params?: object) => void;
    goBack: () => void;
  };
}

const PropertiesPage: React.FC<InvestmentPageProps> = ({ navigation }) => {
  const [isMap, setIsMap] = useState(false);
  const toggleSwitch = () => setIsMap((previousState) => !previousState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uniquePropertyLength, setUniquePropertyLength] = useState<number>(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const { refreshTrigger } = useContext(RefreshContext);
  const [property, setProperty] = useState<IPropertyData[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const isFocused = useIsFocused();
  const fontScale = PixelRatio.getFontScale();
  const isFontScaleLarge = fontScale >= 1.2;
  let userIdToUse: string | null;
  const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
  };
  const fetchPropertyData = async () => {
    setLoading(true);
    try {
      userIdToUse = await getUserIdToUse();
      const response = await ApiService.get<IPropertyData[]>(
        `portal/investor/property/${userIdToUse}`,
      );

      const data = response.data;
      response.data.forEach((item) => {
        item.yourAcres = (item.totalAcres * item.ownershipInSuperEntity) / 100;
        item.remainingAcres =
          (item.remainingAcres * item.ownershipInSuperEntity) / 100;
        const roundedAcres = roundToTwoDecimals(item.remainingAcres);
        const roundedPrice = roundToTwoDecimals(item.potentialPricePerAcres);
        item.potentialMarketValue = roundToTwoDecimals(
          roundedAcres * roundedPrice,
        );
      });
      console.log("the data... ", data);
      setProperty(data);
      calculateUniquePropertyLength(data);
      setLoading(false);
    } catch (error) {
      setError("Error fetching data");
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isFocused && refreshTrigger > 0) {
      setAuthToken();
      fetchPropertyData();
    }

    const handleRefresh = () => {
      fetchPropertyData();
    };
    globalEventEmitter.on("globalRefresh", handleRefresh);
    return () => {
      globalEventEmitter.off("globalRefresh", handleRefresh);
    };
  }, [refreshTrigger]);

  const calculateUniquePropertyLength = (list: IPropertyData[]) => {
    const uniquePropertyNames = new Set<IPropertyData>();

    list.forEach((value) => uniquePropertyNames.add(value));
    setUniquePropertyLength(uniquePropertyNames.size);
  };
  const getTotalForColumn = (columnName: string): number => {
    let total = 0;
    for (const record of property) {
      total += (record as unknown as Record<string, number>)[columnName] || 0;
    }
    return total;
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const renderInvestmentItem = ({ item }: { item: IPropertyData }) => (
    <View
      style={[
        commonStyles.subContainer,
        commonStyles.p14,
        commonStyles.mt20,
        commonStyles.mlar2,
      ]}
    >
      <TouchableOpacity
        onPress={() =>
          navigation.navigate("PropertyDetails", {
            property: item,
            isInvestment: false,
            sourcePage: "Properties",
          })
        }
      >
        <View style={commonStyles.cardItemblock}>
          <OverflowTooltipText
            text={item.name}
            tooltipValue={item.name}
            style={commonStyles.detailsSubHeader}
            testID={`property-name-${item.id}`}
            onPress={() =>
              navigation.navigate("PropertyDetails", {
                property: item,
                isInvestment: false,
                sourcePage: "Properties",
              })
            }
          />
          <AntDesign
            testID={`arrow-button-${item.id}`}
            name="arrow-right"
            size={22}
            color="#666"
            marginBottom={4}
          />
        </View>
        <View style={commonStyles.detailsLabelContainer}>
          <View
            style={{
              flexDirection: isFontScaleLarge ? "column" : "row",
            }}
            testID={`${item.id}`}
          >
            <AppText
              testID={`your-acres-${item.id}`}
              style={commonStyles.detailsLabel}
            >
              Your Acres:{" "}
            </AppText>

            <FormattedText
              testID={`your-acres-value-${item.id}`}
              value={item.yourAcres}
              type="number"
              style={commonStyles.detailsValue}
            />
          </View>
          <View
            style={{
              flexDirection: isFontScaleLarge ? "column" : "row",
            }}
          >

            <AppText
              testID={`potential-value-${item.id}`}
              style={commonStyles.detailsLabel}
            >
              Potential Market Value:{" "}
            </AppText>

            <FormattedText
              testID={`potential-market-value-${item.id}`}
              value={item.potentialMarketValue}
              type="currency"
              style={commonStyles.detailsValue}
            />
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView
      edges={["bottom", "left", "right"]}
      style={[commonStyles.container, commonStyles.screenBottomGap]}
    >
      <View style={commonStyles.headerContainer}>
        <AppText testID="property" style={commonStyles.header}>
          Your Properties
        </AppText>
        <View style={PropertiesStyle.switchContainer}>
          <Switch
            value={isMap}
            onValueChange={toggleSwitch}
            trackColor={{ true: "#281C9D" }}
            thumbColor={isMap ? "#5655B9" : "lightgray"}
            ios_backgroundColor="#281C9D"
          />
        </View>
      </View>

      {isMap ? (
        <View style={PropertiesStyle.mapContainer}>
          {Platform.OS === "web" ? (
            <PropertyMap property={property} />
          ) : (
            <PropertyMapsMobile property={property} />
          )}
        </View>
      ) : (
        <ScrollView
          contentContainerStyle={PropertiesStyle.scrollContainer}
          testID="scrollView"
        >
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.p15,
              commonStyles.mb10,
            ]}
          >
            <TouchableOpacity onPress={toggleExpand}>
              <View style={commonStyles.cardItemblock}>
                <AppText
                  testID="summary"
                  style={[commonStyles.summaryHeader, commonStyles.pl15]}
                >
                  Summary
                </AppText>
                <AntDesign
                  testID={`arrow-icon-${isExpanded ? "up" : "down"}`}
                  name={isExpanded ? "up" : "down"}
                  size={24}
                  color="#666"
                />
              </View>
            </TouchableOpacity>

            {isExpanded && (
              <View
                testID="summary-content"
                style={PropertiesStyle.expandedDetails}
              >
                <View style={commonStyles.summaryItem}>
                  <AppText
                    testID="noOfProperties"
                    style={commonStyles.commonFont}
                  >
                    No of Properties
                  </AppText>
                  {loading ? (
                    <View>
                      <SkeletonLoader rows={1} rowWidth="60%" />
                    </View>
                  ) : (
                    <AppText
                      testID="noOfPropertiesValue"
                      style={commonStyles.summaryAmount}
                    >
                      {uniquePropertyLength}
                    </AppText>
                  )}
                </View>
                <TouchableOpacity
                  style={commonStyles.summaryItem}
                  onPress={() => navigation.navigate("LandBank", { property })}
                >
                  <View style={PropertiesStyle.row}>
                    <AppText
                      testID="yourLandBank"
                      style={commonStyles.commonFont}
                    >
                      Your Land Bank
                    </AppText>
                    <AntDesign
                      testID="land-bank-arrow"
                      name="arrow-right"
                      size={15}
                      color="#666"
                    />
                  </View>
                  <View style={PropertiesStyle.row}>
                    <FormattedText
                      testID="yourLandBankValue"
                      value={getTotalForColumn("remainingAcres")}
                      type="number"
                      style={commonStyles.summaryAmount}
                      loading={loading}
                    />

                    {loading ? null : (
                      <AppText
                        testID="acres"
                        style={commonStyles.summaryAmount}
                      >
                        acres
                      </AppText>
                    )}
                  </View>
                </TouchableOpacity>
                <View style={commonStyles.summaryItem}>

                  <OverflowTooltipText
                    testID="potentialMarketValue"
                    text="Potential Market Value"
                    containerWidth="55%"
                    style={commonStyles.commonFont}
                  />
                  <FormattedText
                    testID="potentialMarketValueValue"
                    value={getTotalForColumn("potentialMarketValue")}
                    type="currency"
                    style={commonStyles.summaryAmount}
                    loading={loading}
                  />
                </View>
              </View>
            )}
          </View>

          <AppText testID="details" style={commonStyles.detailsHeader}>
            Details
          </AppText>

          {loading ? (
            <SkeletonLoader rows={10} />
          ) : (
            <FlatList
              data={property}
              renderItem={renderInvestmentItem}
              keyExtractor={(item, index) =>
                `${item.id}-${index}-${Date.now()}`
              }
              contentContainerStyle={commonStyles.pb16}
              scrollEnabled={Platform.OS == "web" ? true : false}
            />
          )}
        </ScrollView>
      )}
    </SafeAreaView>
  );
};

export default PropertiesPage;
